import { accountsApi } from "@api";
import { tokenStore } from "@services/tokenStore";
import {
  Account,
  AccountExpand,
  ListIncomingPayerPayeeLinkRequests200ResponseDataInner as IncomingLinkRequest
} from "@wingspanhq/wingspan-api-sdk";
import { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useWSMutation } from "../../../query/helpers";
import { analyticsService } from "@services/analyticsService";
import { acceptPendingLinkRequests } from "./acceptPendingLinkRequests";
import { getOnboardingRedirectPath } from "./getOnboardingRedirectPath";
import { useBindAccountToSession } from "./useBindAccountToSession";

export type CurrentAccount = {
  account: Account;
};

export const useInitMemberApp = () => {
  const history = useHistory();
  const location = useLocation();
  const bindAccountToSession = useBindAccountToSession();

  const [init, meta] = useWSMutation<CurrentAccount>(
    async () => {
      const isImpersonating = tokenStore.isImpersonating();

      let account: Account;
      let incomingLinkRequests: IncomingLinkRequest[] = [];

      if (isImpersonating) {
        // Impersonation init

        account = await accountsApi.getAccount({
          accountId: tokenStore.getIdentity()?.accountId as string,
          expand: [AccountExpand.COMPLIANCE_ENTITY]
        });
      } else {
        // Regular flow init

        account = await bindAccountToSession();

        // Sequenced, not parallel: accepting an invitation needs the accepting
        // account in the request body, so the session has to be bound first.
        incomingLinkRequests = await acceptPendingLinkRequests(account.id!);
      }

      analyticsService.identifyAccount(account);

      // PLA-3517: a user who never started onboarding is redirected into it,
      // once, here. `incomingLinkRequests` stays empty under impersonation, so
      // an admin is never thrown into somebody else's onboarding.
      const onboardingRedirectPath = await getOnboardingRedirectPath({
        incomingLinkRequests,
        pathname: location.pathname
      });

      if (onboardingRedirectPath) {
        history.replace(onboardingRedirectPath);
        return { account };
      }

      if (location.pathname === "/member/sign-in") {
        history.replace(
          location.hash ? location.hash.slice(1) : "/member/dashboard"
        );
      }

      return { account };
    },
    {
      onError: error => console.error("Member app init failed:", error)
    }
  );

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isLoading: meta.isIdle || meta.isPending,
    isError: meta.isError,
    currentAccount: meta.data
  };
};
